import { Builder } from '@builder.io/react';
import classNames from 'classnames';
import { pipe } from 'fp-ts/lib/function';

import { Separator, I18nInstance } from '@catawiki/cata-style';

import { fetchAuctions, fetchLotPrices, fetchLots } from '@/shared/api/buyer';
import {
  BACKGROUND_COLOR_NAMES,
  BackgroundClassMap,
  COLOR_NAME_WHITE,
} from '@/shared/constants/colors';
import {
  EnrichedLot as FeaturedLot,
  Lot,
  enrichLotsWithPriceData,
} from '@/shared/models/lot';
import { MockAuction } from '@/shared/stubs/auction';
import { MockLot } from '@/shared/stubs/lot';
import { getBuilderIOComponentClassName } from '@/shared/utils/getBuilderIOComponentClassName';
import { nullableData } from '@/shared/utils/http';
import { TRACER_NAME_BUILDER, getSpan } from '@/shared/utils/tracer';

import { Title } from '../Title';
import { FeaturedAuction, FeaturedAuctions } from './FeaturedAuctions';
import { FeaturedLots } from './FeaturedLots';

interface LotIdInput {
  lotId: number;
}

interface AuctionIdInput {
  auctionId: number;
}

interface BuilderInput {
  title: string;
  backgroundColorClassName: string;
  auctionIds?: AuctionIdInput[];
  lotIds?: LotIdInput[];
  lotsUrl?: string;
  numberOfLots?: number;
  endTime: string;
  featuredAuctionsTitle?: string;
  featuredLotsTitle?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributes?: any;
}

type Props = BuilderInput & {
  locale: string;
  featuredAuctions?: FeaturedAuction[];
  featuredLots?: FeaturedLot[];
};

const shouldShowSeparator = (
  auctionIds?: AuctionIdInput[],
  lotIds?: LotIdInput[]
) => {
  // If there is only auctions or only lots, don't show the separator
  if (!auctionIds || !lotIds) return false;
  if (!auctionIds.length) return false;
  if (!lotIds.length) return false;
  return true;
};

function Divider({ hidden = false }: { hidden?: boolean }) {
  if (hidden) return null;
  return (
    <div className="tw:flex tw:flex-col u-g-xl">
      <Separator color="light-gray" />
    </div>
  );
}

export const DayAuction = ({
  title,
  endTime,
  backgroundColorClassName,
  featuredAuctions = [],
  featuredAuctionsTitle,
  lotsUrl = '',
  featuredLots = [],
  featuredLotsTitle,
  lotIds = [],
  numberOfLots = 5,
  auctionIds = [],
  locale,
  attributes,
}: Props) => {
  const styles = getBuilderIOComponentClassName(attributes);
  const spacing = styles || classNames('u-p-t-xxl-6', 'u-p-t-sm-xxl-4');

  const endDate = new Date(endTime);
  if (Date.now() > endDate.getTime()) return null;

  let componentLots = featuredLots;
  let componentAuctions = featuredAuctions;
  if (Builder.isEditing || Builder.isPreviewing) {
    if (lotIds.length > 0 && featuredLots.length === 0) {
      componentLots = lotIds.map((lot) => MockLot({ locale, id: lot.lotId }));
    }

    if (auctionIds.length > 0 && featuredAuctions.length === 0) {
      componentAuctions = auctionIds.map((auction) =>
        MockAuction(locale, auction.auctionId)
      );
    }
  }

  return (
    <div {...attributes} className={classNames(backgroundColorClassName)}>
      <div className={classNames('u-wrapper', spacing)}>
        <Title title={title} />
        <FeaturedAuctions
          className={classNames('u-m-b-sm-xxl-4', 'u-m-b-xxl-6', 'u-m-t-xl')}
          title={featuredAuctionsTitle}
          auctions={componentAuctions}
          hidden={!auctionIds.length}
        />
        <Divider hidden={!shouldShowSeparator(auctionIds, lotIds)} />
        <FeaturedLots
          className={classNames(
            'u-m-b-sm-xxl-4',
            'u-m-t-sm-xxl4',
            'u-m-b-xxl-6',
            'u-m-t-xxl-6'
          )}
          title={featuredLotsTitle}
          lots={componentLots}
          numberOfLots={numberOfLots}
          url={lotsUrl}
          hidden={!lotIds.length}
        />
      </div>
    </div>
  );
};

async function fetchFeaturedAuctions(
  locale: string,
  auctionIds: { auctionId: number }[]
): Promise<FeaturedAuction[]> {
  if (auctionIds.length === 0) return [];

  const ids = auctionIds.map((item) => item.auctionId);
  const eitherAuctions = await fetchAuctions(locale, ids);
  const remoteAuctions = pipe(eitherAuctions, nullableData) || [];
  const auctionsMap = remoteAuctions.reduce(
    (out, auction) => ({
      ...out,
      [auction.id]: {
        id: auction.id,
        href: auction.url,
        title: auction.title,
        curator: auction.auctioneers[0],
        mainImage: auction.promo_images[0].medium,
        thumbImages: auction.promo_images
          .map((item) => item.medium)
          .slice(1, 4) as [string, string, string],
        numberOfLots: auction.lot_count,
        closeAt: new Date(auction.close_at).getTime(),
      },
    }),
    {} as {
      [key in number]: FeaturedAuction;
    }
  );
  return ids.map((id) => auctionsMap[id] || MockAuction(locale, id));
}

async function fetchFeaturedLots(
  i18n: I18nInstance,
  locale: string,
  lotIds: { lotId: number }[]
): Promise<FeaturedLot[]> {
  if (lotIds.length === 0) return [];
  const ids = lotIds.map((item) => item.lotId);

  const [eitherLots, eitherLotPrices] = await Promise.all([
    fetchLots(locale, ids),
    fetchLotPrices(ids),
  ]);

  const remoteLots = pipe(eitherLots, nullableData) || [];
  const lotPrices = pipe(eitherLotPrices, nullableData) || [];

  const lotsMap = remoteLots.reduce(
    (out, lot) => ({
      ...out,
      [lot.id]: {
        id: lot.id,
        imageUrl: lot.original_image_url,
        lotUrl: lot.url,
        title: lot.title,
      },
    }),

    {} as { [key in number]: Lot }
  );

  const lots = ids.map((id) => lotsMap[id] || MockLot({ locale, id }));
  const lotsWithPriceData = enrichLotsWithPriceData(i18n, lots, lotPrices);

  return lotsWithPriceData;
}

export function dataFetcher(locale: string, i18n: I18nInstance) {
  return async ({ auctionIds = [], lotIds = [] }: BuilderInput) => {
    const span = getSpan(TRACER_NAME_BUILDER, 'DayAuction.dataFetcher');
    const [featuredAuctions, featuredLots] = await Promise.all([
      fetchFeaturedAuctions(locale, auctionIds),
      fetchFeaturedLots(i18n, locale, lotIds),
    ]);
    span.end();
    return {
      locale,
      featuredAuctions,
      featuredLots,
    };
  };
}

export function componentRegister() {
  const name = 'DayAuction';
  Builder.registerComponent(DayAuction, {
    name,
    inputs: [
      { name: 'title', type: 'string' },
      {
        name: 'endTime',
        type: 'date',
      },
      { name: 'featuredAuctionsTitle', type: 'string' },
      { name: 'featuredLotsTitle', type: 'string' },
      {
        name: 'backgroundColorClassName',
        friendlyName: 'Background Color',
        type: 'string',
        defaultValue: BackgroundClassMap[COLOR_NAME_WHITE],
        enum: BACKGROUND_COLOR_NAMES.map((value) => ({
          label: value,
          value: BackgroundClassMap[value],
        })),
      },
      {
        name: 'auctionIds',
        type: 'list',
        subFields: [{ name: 'auctionId', type: 'number' }],
      },
      {
        name: 'lotIds',
        type: 'list',
        subFields: [{ name: 'lotId', type: 'number' }],
      },
      {
        name: 'lotsUrl',
        type: 'string',
      },
      {
        name: 'numberOfLots',
        type: 'number',
        defaultValue: 5,
        min: 4,
        max: 5,
      },
    ],
    hideFromInsertMenu: true,
    noWrap: true,
  });
  return name;
}
